/**
 * useEmailTemplates Hook
 *
 * Manages email template + folder state including tree structure and selection.
 * Fetches both folders and templates in parallel on mount.
 * Exposes invalidate() to trigger re-fetch after mutations.
 */

import { useState, useMemo, useEffect, useCallback } from 'react'
import {
  buildTemplateTree,
  mapApiToEmailTemplate,
  type EmailTemplate,
  type TemplateNode,
  type EmailTemplateListItem,
} from '@/lib/email-templates'
import type { EmailFolder } from '@/lib/email-templates/folder-types'

const getBackendUrl = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  return ''
}

interface UseEmailTemplatesOptions {
  initialTemplateId?: string | null
}

interface UseEmailTemplatesResult {
  templateTree: TemplateNode
  selectedTemplate: EmailTemplate | null
  selectedId: string | null
  selectTemplate: (id: string) => void
  clearSelection: () => void
  templateCount: number
  folders: EmailFolder[]
  isLoading: boolean
  error: string | null
  invalidate: () => void
}

export function useEmailTemplates(options?: UseEmailTemplatesOptions): UseEmailTemplatesResult {
  const [selectedId, setSelectedId] = useState<string | null>(options?.initialTemplateId ?? null)
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [folders, setFolders] = useState<EmailFolder[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const invalidate = useCallback(() => setRefreshKey((k) => k + 1), [])

  // Fetch folders + templates in parallel
  useEffect(() => {
    const isInitialLoad = refreshKey === 0
    const fetchData = async () => {
      if (isInitialLoad) setIsLoading(true)
      try {
        const backendUrl = getBackendUrl()
        const [foldersRes, templatesRes] = await Promise.all([
          fetch(`${backendUrl}/api/equip/email-folders`, { credentials: 'include' }),
          fetch(`${backendUrl}/api/equip/email-templates`, { credentials: 'include' }),
        ])

        if (!foldersRes.ok) throw new Error(`Failed to fetch folders: ${foldersRes.status}`)
        if (!templatesRes.ok) throw new Error(`Failed to fetch templates: ${templatesRes.status}`)

        const [foldersData, templatesData]: [EmailFolder[], EmailTemplateListItem[]] =
          await Promise.all([foldersRes.json(), templatesRes.json()])

        setFolders(foldersData)
        setTemplates(templatesData.map(mapApiToEmailTemplate))
        setError(null)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch data'
        setError(message)
        console.error('Error fetching templates/folders:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [refreshKey])

  // Build tree from both folders and templates
  const templateTree = useMemo(
    () => buildTemplateTree(templates, folders),
    [templates, folders]
  )

  // Handle initial template ID changes (deep-linking)
  useEffect(() => {
    if (options?.initialTemplateId && templates.length > 0) {
      const template = templates.find((t) => t.id === options.initialTemplateId)
      if (template) {
        setSelectedId(options.initialTemplateId)
      }
    }
  }, [options?.initialTemplateId, templates])

  const selectedTemplate = useMemo(() => {
    if (!selectedId || templates.length === 0) return null
    return templates.find((t) => t.id === selectedId) ?? null
  }, [selectedId, templates])

  return {
    templateTree,
    selectedTemplate,
    selectedId,
    selectTemplate: setSelectedId,
    clearSelection: () => setSelectedId(null),
    templateCount: templates.length,
    folders,
    isLoading,
    error,
    invalidate,
  }
}
